/**
 * The metadata field set as data: the form value's shape, its empty value,
 * its field keys, their display names and the derived-field list.
 *
 * A leaf on purpose. `constants/assetTypes.ts` types its registry with
 * `MetadataFieldKey`, `api.ts` types its payloads with `CopyElement`, and the
 * form in `components/metadata/MetadataFields.tsx` reads the registry, so
 * these must live below all of them or they import each other. Nothing here
 * imports anything.
 */

/** One Copy / Dynamic Elements row: a content type plus free text. */
export interface CopyElement {
  /** One of `COPY_TYPE_OPTIONS` in `constants/taxonomy.ts`. */
  type: string;
  value: string;
}

export interface MetadataFormValue {
  // Three independent multi-selects; nothing is derived between them.
  nameplate: string[];
  business_unit: string[];
  carline_code: string[];
  tier: string[];
  ai_indicator: string;
  source_agency: string;
  domain: string;
  // Source images only; a checkbox, so never blank
  is_lifestyle_image: boolean;

  // Usage rights: what the licence covers, by channel and by market
  channel_rights: string[];
  country_rights: string[];

  // Static specifications
  expiry_rights: string;
  copy_elements: CopyElement[];
  platform_rights: string[];

  // Master image format; the channel follows from the pair. A film has the
  // size alone, as its aspect ratio.
  format_type: string;
  format_size: string;

  // Filmic length in seconds, as a select's string ('' or '15'); declared at
  // upload, never read from the video
  duration_seconds: string;

  // Filmic legacy tracking (FCV 1.0 id)
  legacy_fcv_id: string;

  // OLA master fields (campaign, variation, ticket, briefing, notes)
  campaign_name: string;
  creative_variation: string;
  production_ticket_ref: string;
  briefing_form_ref: string;
  notes: string;
}

/**
 * The fields the OLA upload form hands over to the source image when one is
 * picked: shown read-only from it and left out of the request, because the
 * backend overwrites them with the source image's values.
 *
 * This is the backend's `INHERITED_FROM_SOURCE`
 * (`apps/asset-library/backend/src/core/asset_types.py`) minus `ai_indicator`.
 * The backend inherits that one too, but the form still offers the select, so
 * a value typed there is discarded when the source image carries one — a
 * known gap, older than this list, tracked separately. The drawer's warning
 * list in `components/grid/sourceImageInheritance.ts` carries all ten.
 */
export const DERIVED_FROM_SOURCE = [
  'nameplate',
  'carline_code',
  'business_unit',
  'tier',
  'expiry_rights',
  'source_agency',
  'channel_rights',
  'country_rights',
  'domain',
] as const satisfies readonly MetadataFieldKey[];

export type MetadataFieldKey = keyof MetadataFormValue;

export function emptyMetadataValue(): MetadataFormValue {
  return {
    nameplate: [],
    business_unit: [],
    carline_code: [],
    tier: [],
    ai_indicator: '',
    source_agency: '',
    domain: '',
    is_lifestyle_image: false,
    channel_rights: [],
    country_rights: [],
    expiry_rights: '',
    copy_elements: [],
    platform_rights: [],
    format_type: '',
    format_size: '',
    duration_seconds: '',
    legacy_fcv_id: '',
    campaign_name: '',
    creative_variation: '',
    production_ticket_ref: '',
    briefing_form_ref: '',
    notes: '',
  };
}

/**
 * The display name of each field, in one place.
 *
 * Every message that names a field reads from here — the form's own labels,
 * the source image overwrite warning. A field the user
 * is told about under a name the form doesn't use is a field they can't find.
 */
export const METADATA_FIELD_LABELS: Record<MetadataFieldKey, string> = {
  nameplate: 'Nameplate',
  business_unit: 'Business unit',
  carline_code: 'Carline code',
  tier: 'Tier',
  ai_indicator: 'AI indicator',
  source_agency: 'Creative agency',
  domain: 'Domain',
  is_lifestyle_image: 'Lifestyle image',
  channel_rights: 'Channel rights',
  country_rights: 'Country rights',
  expiry_rights: 'Expiry rights',
  copy_elements: 'Copy / dynamic elements',
  platform_rights: 'Platform rights',
  format_type: 'Format type',
  format_size: 'Format size',
  duration_seconds: 'Duration',
  legacy_fcv_id: 'Legacy FCV ID',
  campaign_name: 'Campaign name',
  creative_variation: 'Creative variation',
  production_ticket_ref: 'Production ticket',
  briefing_form_ref: 'Briefing form',
  notes: 'Notes',
};
